import store from '~/store/Store';
import { getDiamGroupId } from '~/domain/uptime/utils/utils';

function createGetRequest(
  endpoint: string,
  parameters?: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean,
  omitCredentials?: boolean
): Request {
  let url = endpoint;
  if (parameters) {
    url = url.concat('?', objectToGetParameters(parameters));
  }
  return new Request(url, {
    method: 'GET',
    headers: createHeaders(token, provider, includeVersionHeaders),
    mode: 'cors',
    credentials: omitCredentials ? undefined : 'include',
  });
}

function createNoTokenGetRequest(
  endpoint: string,
  parameters?: unknown,
  includeVersionHeaders?: boolean
): Request {
  return createGetRequest(endpoint, parameters, null, null, includeVersionHeaders, true);
}

function createPostRequest(
  endpoint: string,
  body?: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean,
  omitCredentials?: boolean
): Request {
  return createRequestWithBody(
    'POST',
    endpoint,
    body,
    token,
    provider,
    includeVersionHeaders,
    omitCredentials
  );
}

function createNoTokenPostRequest(
  endpoint: string,
  body?: unknown,
  includeVersionHeaders?: boolean
): Request {
  return createPostRequest(endpoint, body, null, null, includeVersionHeaders, true);
}

function createDeleteRequest(
  endpoint: string,
  body?: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean
): Request {
  return createRequestWithBody('DELETE', endpoint, body, token, provider, includeVersionHeaders);
}

function createNoTokenDeleteRequest(
  endpoint: string,
  body?: unknown,
  includeVersionHeaders?: boolean
): Request {
  return createDeleteRequest(endpoint, body, null, null, includeVersionHeaders);
}

function createPutRequest(
  endpoint: string,
  body: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean
): Request {
  return createRequestWithBody('PUT', endpoint, body, token, provider, includeVersionHeaders);
}

function createNoTokenPutRequest(
  endpoint: string,
  body: unknown,
  includeVersionHeaders?: boolean
): Request {
  return createPutRequest(endpoint, body, null, null, includeVersionHeaders);
}

function createPatchRequest(
  endpoint: string,
  body: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean,
  omitCredentials?: boolean
): Request {
  return createRequestWithBody(
    'PATCH',
    endpoint,
    body,
    token,
    provider,
    includeVersionHeaders,
    omitCredentials
  );
}

function createNoTokenPatchRequest(
  endpoint: string,
  body: unknown,
  includeVersionHeaders?: boolean
): Request {
  return createPatchRequest(endpoint, body, null, null, includeVersionHeaders);
}

export function isStatusError(httpStatus): boolean {
  const statusSuccess = /2\d{2}/g;
  return !statusSuccess.test(httpStatus);
}

const getAppTimestampFromStore = () => {
  const appStateRecord = store.getState().app;
  return appStateRecord.timestamp;
};

export function createHeaders(
  token: string | undefined | null = store.getState().authentication.token,
  provider: string | undefined | null = store.getState().authentication.provider,
  includeVersionHeaders = true
): Headers {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (includeVersionHeaders) {
    const timestamp = getAppTimestampFromStore();
    if (timestamp) {
      headers['x-app-timestamp'] = timestamp;
    }
  }
  const groupId = getDiamGroupId();
  if (groupId !== undefined) {
    headers['x-diam-group-id'] = groupId;
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    if (provider) {
      headers['Authorization-Provider'] = provider;
    }
  }

  return new Headers(headers);
}

export function objectToGetParameters(obj: unknown): string {
  return Object.entries(obj as Record<any, any>)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

function createRequestWithBody(
  method: string,
  endpoint: string,
  body?: unknown,
  token?: string | null,
  provider?: string | null,
  includeVersionHeaders?: boolean,
  omitCredentials?: boolean
): Request {
  return new Request(endpoint, {
    method,
    headers: createHeaders(token, provider, includeVersionHeaders),
    mode: 'cors',
    body: JSON.stringify(body),
    credentials: omitCredentials ? undefined : 'include',
  });
}

export default {
  createGetRequest,
  createNoTokenGetRequest,
  createPostRequest,
  createNoTokenPostRequest,
  createDeleteRequest,
  createNoTokenDeleteRequest,
  createPutRequest,
  createNoTokenPutRequest,
  createPatchRequest,
  createNoTokenPatchRequest,
};
